import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { IamClient } from '@evidentid/iam-client';
import { SsoGroupRuleView, SsoProvisioningView } from '@evidentid/tprm-portal-lib/models/sso-provisioning';

import { isAuthFailure } from './utils/authFailure';
import { readCallerGroups } from './utils/selfLockout';

interface SsoProvisioningRequirement {
    gatewayClient: GatewayApiClient;
    auth: IamClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): SsoProvisioningRequirement {
    return store.$app;
}

interface SsoProvisioningState {
    // Keyed per relying party. Absent = never loaded; null = the load failed.
    view: Record<string, SsoProvisioningView | null>;
    // The parsed rejection body of the last failed save per relying party (carries the API's
    // validation message), cleared when the next save starts.
    saveError: Record<string, any>;
    // The signed-in user's own IdP groups, for the self-lockout check. Not per relying party: it
    // is a property of the login, not of the tenant being edited. `null` means unknown -- never
    // loaded, or the login carries no groups claim -- and the check stands aside.
    callerGroups: string[] | null;
}

export const useSsoProvisioningStore = defineStore('ssoProvisioning', {
    state: (): SsoProvisioningState => ({
        view: {},
        saveError: {},
        callerGroups: null,
    }),
    actions: {
        // Reads the claim off the ID token the app already holds; no network call. A failure is
        // indistinguishable from a login with no groups, and both mean the same thing here: the
        // self-lockout check does not run.
        async loadCallerGroups(): Promise<void> {
            try {
                const auth = appOf(this).auth;
                const [ idToken, accessToken ] = await Promise.all([ auth.getIdToken(), auth.getAccessToken() ]);
                this.callerGroups = readCallerGroups(idToken, accessToken);
            } catch {
                this.callerGroups = null;
            }
        },
        // `quiet` is for callers that only want the view as a hint: a failure still records null
        // but never raises the persisting-error screen.
        async load(rpName: string, options: { quiet?: boolean } = {}): Promise<boolean> {
            try {
                const view = await appOf(this).gatewayClient.tprmUi().getSsoProvisioning(rpName);
                this.view = { ...this.view, [rpName]: view };
                return true;
            } catch (error) {
                this.view = { ...this.view, [rpName]: null };
                // Auth failures re-authenticate via the global screen; anything else is the page's
                // own inline failure state. Tested through `isAuthFailure`, not on the status: the
                // gateway client never stamps one on the rejections this cares about.
                if (!options.quiet && isAuthFailure(error)) {
                    await getPersistingErrorActions().showError(error);
                }
                return false;
            }
        },
        async setEnabled(rpName: string, enabled: boolean): Promise<boolean> {
            return this.runSave(rpName, async () => {
                await appOf(this).gatewayClient.tprmUi().putSsoProvisioningEnabled(rpName, enabled);
                // Safe to assert locally: the setting lives in one place, the Auth0 connection, and the
                // endpoint fails the request unless the push to it succeeded. A 200 therefore means this is
                // the value. Re-reading would cost another pass over Auth0's connection list, which the
                // server-side lookup already flags as rate-limit sensitive.
                this.patchView(rpName, { dynamicCreationEnabled: enabled });
            });
        },
        async saveRules(rpName: string, rules: SsoGroupRuleView[]): Promise<boolean> {
            return this.runSave(rpName, async () => {
                await appOf(this).gatewayClient.tprmUi().putSsoProvisioningRules(rpName, rules);
                this.patchView(rpName, { rules });
            });
        },
        patchView(rpName: string, patch: Partial<SsoProvisioningView>) {
            const current = this.view[rpName];
            if (current) {
                this.view = { ...this.view, [rpName]: { ...current, ...patch } };
            }
        },
        /** A failed save never touches the loaded view, and only an auth failure leaves the page.
         *
         * 🚨 THE TEST IS "IS IT AN AUTH FAILURE", NOT "IS IT A 400". `showError` full-page-routes to
         * `error.application`, which unmounts this view and DESTROYS THE UNSAVED DRAFT -- so routing
         * on everything-but-400 threw away the admin's work for a 409 and a 500 alike. The 409 is
         * the most likely save failure there is (the tenant's Auth0 connection has gone, or belongs
         * to another tenant) and it carries a `message` the page renders verbatim, which is no use
         * to anyone on a page that has already been replaced.
         *
         * An auth failure is different in kind: the session is gone, so there is nothing to save the
         * draft for, and the global screen is what re-authenticates.
         */
        async runSave(rpName: string, save: () => Promise<void>): Promise<boolean> {
            this.saveError = { ...this.saveError, [rpName]: null };
            try {
                await save();
                return true;
            } catch (error) {
                this.saveError = { ...this.saveError, [rpName]: error };
                if (isAuthFailure(error)) {
                    await getPersistingErrorActions().showError(error);
                }
                return false;
            }
        },
    },
});
